import { createUseStyles } from 'react-jss'
import Link from '../Link'
import { Colors } from '../../constants/colors'
import Card from '../Card'
import { CreditOverviewItemModel } from '../../hooks/useCreditOverviewActions'
import { formatMoney } from '../../utils/moneyHelper'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faExclamationTriangle } from '@fortawesome/free-solid-svg-icons'
import { CreditRiskClassification } from '../../redux/companyMonitorTypes'

export interface Props {
  items: CreditOverviewItemModel[]
}

const CreditOverviewList = ({ items }: Props) => {
  const styles = useStyles()

  const getRiskClassColor = (creditRiskClassification: CreditRiskClassification | undefined) : string => {
    switch (creditRiskClassification) {
      case CreditRiskClassification.veryHigh:
        return Colors.rubel_900
      case CreditRiskClassification.high:
        return 'orange'
      case CreditRiskClassification.medium:
      case CreditRiskClassification.low:
      case CreditRiskClassification.veryLow:
        return Colors.green
    }

    return Colors.rubel_900
  }

  const renderHeader = () => {
    return (
      <Card className={`${styles.container} ${styles.header} card`}>
        <div className={styles.largeCell}>
          <h6>Company</h6>
        </div>
        <div className={styles.defaultCell}>
          <h6>Registration number</h6>
        </div>
        <div className={styles.defaultCell}>
          <h6>Approved credit</h6>
        </div>
        <div className={styles.defaultCell}>
          <h6>Available credit</h6>
        </div>
        <div className={styles.defaultCell}>
          <h6>Current usage</h6>
        </div>
        <div className={styles.defaultCell}>
          <h6>Recent max usage</h6>
        </div>
        <div className={styles.smallCell}>
          <h6>Score</h6>
        </div>
        <div className={styles.defaultCell}>
          <h6>Risk class</h6>
        </div>
        <div className={styles.smallCell}>
          <h6>POD%</h6>
        </div>
        <div className={styles.defaultCell}>
          <h6>Current risked amount</h6>
        </div>
        <div className={styles.defaultCell}>
          <h6>Recent max risked amount</h6>
        </div>
      </Card>
    )
  }

  const renderListItems = () => {
    return items?.map((item, index) => {
      return (
        <Card
          key={`credit_overview_${item.customerId}`}
          className={`${styles.container} card`}
        >
          <div className={styles.largeCell}>
            <Link to={`/customers/${item.customerId}`}>
              {item.name}
            </Link>
          </div>
          <div className={styles.defaultCell}>
            {item.companyRegistrationNumber}
          </div>
          <div className={styles.defaultCell}>
            {item.approvedCreditLimit != null ? formatMoney(item.approvedCreditLimit) : '-'}
          </div>
          <div className={styles.defaultCell}>
            {item.availableCreditLimit != null ? formatMoney(item.availableCreditLimit) : '-'}
          </div>
          <div className={styles.defaultCell}>
            {item.currentCreditUsage != null ? formatMoney(item.currentCreditUsage) : '-'}
          </div>
          <div className={styles.defaultCell}>
            {item.recentMaxCreditUsage != null ? formatMoney(item.recentMaxCreditUsage) : '-'}
          </div>
          <div className={styles.smallCell}>
            <span style={{color: getRiskClassColor(item.creditRiskClassification)}}>{item.creditScore}</span>
          </div>
          <div className={styles.defaultCell}>
            <span style={{color: getRiskClassColor(item.creditRiskClassification)}}>{item.creditRiskClassification}</span>
          </div>
          <div className={styles.smallCell}>
            {item.probabilityOfDefaultPercent == null &&
              <FontAwesomeIcon
                icon={faExclamationTriangle}
                color={Colors.yellow}
                title='Missing (defaults to 25%)'
              />
            }
            {item.probabilityOfDefaultPercent != null && item.probabilityOfDefaultPercent.toLocaleString('sv-SE')}
          </div>
          <div className={styles.defaultCell}>
            {item.currentRiskedAmount != null ? formatMoney(item.currentRiskedAmount) : '-'}
          </div>
          <div className={styles.defaultCell}>
            {item.recentMaxRiskedAmount != null ? formatMoney(item.recentMaxRiskedAmount) : '-'}
          </div>
        </Card>
      )
    })
  }

  return (
    <div>
      {renderHeader()}
      {renderListItems()}
    </div>
  )
}

export default CreditOverviewList

const useStyles = createUseStyles({
  container: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    textAlign: 'left',
    padding: '3px 20px',
    margin: '10px 0',
    transition: '0.4s',
    '&:hover': {
      backgroundColor: Colors.darkGrayHover,
    }
  },
  header: {
    margin: '30px 0 20px 0',
  },
  largeCell: {
    flex: 5
  },
  defaultCell: {
    flex: 2
  },
  smallCell: {
    flex: 1
  },
  iconCell: {
    textAlign: 'center',
    flex: '0 0 80px'
  }
})
